import React, {useCallback, useState} from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import {useAppState, useAuthService, useDataClientService} from "@stores/AppStateContext";

import {MaterialUiInput} from "../../../components/form/material-ui-input/MaterialUiInput";
import {ModalConfirm} from "../../../components/ModalConfirm";
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {Select} from "../../../components/form/Select";
import {createWorkspace} from "../../../../actions/workspace/createWorkspace";
import {createWorkspaceUser} from "../../../../actions/workspaceUser/createWorkspaceUser";
import {getAllUserOptions} from "../../../../utils/getUserOptions";
import {ensureSlug} from "../../../../utils/validation/ensureSlug";
import {selectWorkspaceFromPlain} from "../../../../actions/ui/selectWorkspace";

import {snackBarRef} from "@stores/snackbarRef";
import {MODALS} from '../modal-names';


import "./CreateWorkspace.css"


const CreateWorkspaceBase = () => {
    const appState = useAppState();
    const rootStore = appState.rootStore;
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    const authService = useAuthService();
    const dataClients = useDataClientService();

    const [selectedUsers, setSelectedUsers] = useState<string[] | null>(null);
    const [wsName, setWsName] = useState<string>('');
    const handleClose = useCallback(() => {
        appState.setModal(null)
    }, []);
    const activeModal = appState.topModal;
    const enqueueSnackbar = snackBarRef.current!.enqueueSnackbar;

    const handleConfirm = async () => {
        try {
            const allWorkspaces = dsManager?.workspacesDS.allRows!;

            let defaultName = wsName.trim() || intl.get('workspace.no-name').d('New Workspace');
            let uniqueName = defaultName;
            let uniqueSlug = await ensureSlug(uniqueName);
            let counter = 1;

            while (allWorkspaces.some(ws => ws.slug! === uniqueSlug)) {
                uniqueName = `${defaultName}-${counter}`;
                uniqueSlug = await ensureSlug(uniqueName)
                counter++;
            }

            const usersToAdd = new Set(selectedUsers || []);
            const workspace = await createWorkspace(
                dataClients,
                authService,
                userState,
                uniqueName
            );

            if (!userState!.isCurrentUserOwner && !usersToAdd.has(userState!.currentUserId!)) {
                usersToAdd.add(userState!.currentUserId!);
            }

            for (const userId of usersToAdd) {
                const userRole = userState!.getUserRole(userState!.usersDS.users.get(userId)) || 'reader';
                await createWorkspaceUser(dataClients,
                    authService,
                    userState,
                    workspace.id, userId, userRole);
            }

            appState.setModal(null);
            selectWorkspaceFromPlain(rootStore, dsManager, workspace);
            enqueueSnackbar({
                message: intl.get('workspace.created.snackbar').d('Workspace created'),
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during add members:', error);
        }
    };

    const userOptions = getAllUserOptions(userState);

    return (
        <ModalConfirm
            onConfirm={handleConfirm}
            confirmText={intl.get('create_workspaceModal.button').d("Create workspace")}
            onCancel={handleClose}
            header={intl.get('create_workspaceModal.header').d('Create workspace')}
            open={activeModal === MODALS.CREATE_WORKSPACE}
            onClose={handleClose}
            subtitle={intl.get('create_workspaceModal.topText').d('Create workspaces organized by branch, department, team, project, location and other categories.')}
            children={
                <>
                    <div>
                        <MaterialUiInput
                            fullWidth={true}
                            isRequired={true}
                            name="name"
                            label={intl.get('create_workspaceModal.name_field').d('Name')}
                            type="plain"
                            value={wsName}
                            onChange={setWsName}
                        />
                        <Select
                            className="createWorkspace__SelectUsers"
                            selectedValues={selectedUsers}
                            options={userOptions}
                            placeholder={intl.get('create_workspaceModal.select_users').d('Add members (optional)')}
                            size="large"
                            withBorder={true}
                            onChange={(newValues) => {
                                setSelectedUsers(newValues);
                            }}
                            allowFilter={true}
                            isFullWidth={true}
                            allowMulti={true}
                            withLabel={true}
                        />
                    </div>
                    <div className={"createWorkspace__SelectUsers__description"}>
                        <InfoIcon size={16}/>
                        <span className="createWorkspace__SelectUsers__descriptionText body-small">
                            {intl.get('create_workspaceModal.bottomText').d('Access rights (roles) of added members are currently the same across all workspaces.')}
                        </span>
                    </div>
                </>
            }
        />
    );
}

export const CreateWorkspace = observer(CreateWorkspaceBase);